export const DEFAULT_RUBRICS = {
  "default-3x3": {
    id: "default-3x3",
    name: "Default Audio Rubric 3x3",
    content: [
      [
        "Pronunciation is clear and accurate, with native-like articulation. Errors are rare and do not affect comprehension.",
        "Pronunciation is generally clear with some non-native patterns. Errors are occasional and do not obscure meaning.",
        "Pronunciation errors are frequent and interfere with comprehension. Listener effort is required to understand.",
      ],
      [
        "Speech is fluid and natural, with appropriate pacing and minimal hesitations. Ideas are well-connected.",
        "Speech is mostly smooth, with some hesitations. Pauses do not significantly disrupt the flow of ideas.",
        "Speech is often disrupted by pauses and hesitations. Ideas are disconnected, requiring listener effort.",
      ],
      [
        "Intonation and stress are used effectively to convey meaning. Voice modulation is appropriate and enhances communication.",
        "Basic intonation patterns are present, with mostly correct stress placement. Some voice modulation is attempted.",
        "Intonation is monotone with frequent stress errors. Limited use of voice modulation.",
      ],
    ],
    rows: [
      "Pronunciation & Articulation",
      "Fluency & Flow",
      "Intonation & Stress",
    ],
    cols: ["0/2", "1/2", "2/2"],
  },
  "default-4x4": {
    id: "default-4x4",
    name: "Default Audio Rubric 4x4",
    content: [
      [
        "Pronunciation is near-native, with excellent control of sound patterns. Articulation is clear and precise.",
        "Pronunciation is clear with minor errors. Most sounds are correctly formed, with rare interference in understanding.",
        "Pronunciation shows noticeable accent and some sound confusion. Listener effort is needed for understanding.",
        "Pronunciation issues frequently impede communication. Many sounds are incorrectly formed.",
      ],
      [
        "Fluency is exceptional, with natural rhythm and pacing. Complex ideas are expressed smoothly.",
        "Fluency is good, with occasional natural pauses. Speech flows well most of the time.",
        "Fluency is interrupted by noticeable pauses. Some ideas are choppy and disconnected.",
        "Fluency is poor, with frequent unnatural pauses. Ideas are often disconnected.",
      ],
      [
        "Stress and rhythm patterns are masterful, with excellent word and sentence stress.",
        "Rhythm control is good, with mostly correct stress patterns.",
        "Basic rhythm patterns are present but inconsistent. Some major stress errors.",
        "Limited attention to rhythm and stress. Many stress placement errors.",
      ],
      [
        "Grammar is complex and used accurately. Verb tenses and word order are consistently correct.",
        "Grammar control is good, with some advanced structures. Minor errors do not impede understanding.",
        "Basic grammar is used with frequent errors. Some major errors in verb tenses or word order.",
        "Grammar accuracy is limited, with many basic errors impacting comprehension.",
      ],
    ],
    rows: [
      "Pronunciation Accuracy",
      "Speech Fluency",
      "Rhythm & Connected Speech",
      "Grammatical Control",
    ],
    cols: ["0/3", "1/3", "2/3", "3/3"],
  },
  "default-5x5": {
    id: "default-5x5",
    name: "Default Audio Rubric 5x5",
    content: [
      [
        "Pronunciation is native-like, with perfect control of all sound patterns. No noticeable accent.",
        "Pronunciation is near-native, with rare minor errors. Accent is minimal and does not impede understanding.",
        "Pronunciation is good, with occasional errors. Accent is noticeable but rarely affects comprehension.",
        "Pronunciation issues are noticeable, with several incorrect sound patterns. Accent sometimes interferes with understanding.",
        "Pronunciation problems are major, with many incorrect sounds. Accent significantly impedes comprehension.",
      ],
      [
        "Fluency is exceptional, comparable to native speech. Ideas flow seamlessly with natural hesitations.",
        "Fluency is very good, with minimal unnatural pauses. Ideas connect smoothly.",
        "Fluency is good, with some hesitation. Most ideas flow naturally.",
        "Fluency is limited, with frequent pauses. Ideas often seem disconnected.",
        "Fluency is poor, with many long pauses. Ideas are very disconnected.",
      ],
      [
        "Intonation control is masterful, conveying subtle meanings. Stress patterns are perfect.",
        "Intonation is very good, with rare errors. Stress is nearly always correct.",
        "Basic intonation patterns are good. Most stress is correct.",
        "Intonation is basic, with frequent errors. Many stress mistakes.",
        "Intonation control is poor, with incorrect stress patterns.",
      ],
      [
        "Vocabulary is rich and precise, used appropriately. Excellent word choice and idiomatic expressions.",
        "Vocabulary range is strong, with good precision. Minor word choice errors.",
        "Vocabulary is adequate for basic ideas. Some word choice issues.",
        "Vocabulary is basic, with frequent errors. Word choice is often inappropriate.",
        "Vocabulary is very limited, with many inappropriate word choices.",
      ],
      [
        "Grammar is complex and used accurately. Perfect control of all structures.",
        "Grammar is strong, with rare minor errors. Good control of complex structures.",
        "Basic grammar is good, with some errors. Limited complex structures used.",
        "Grammar is basic, with frequent errors. Complex structures are avoided or incorrect.",
        "Grammar control is poor, with many basic errors impeding understanding.",
      ],
    ],
    rows: [
      "Pronunciation & Accent",
      "Fluency & Transitions",
      "Intonation & Expression",
      "Vocabulary & Idioms",
      "Grammatical Range",
    ],
    cols: ["0/4", "1/4", "2/4", "3/4", "4/4"],
  },
  "essay-3x3": {
    id: "essay-3x3",
    name: "Default Essay Rubric 3x3",
    content: [
      [
        "Main ideas are clearly expressed with appropriate language. Arguments flow logically with effective use of connecting words.",
        "Main ideas are generally clear but may lack some precision. Basic connecting words are used to link arguments.",
        "Main ideas are unclear or confusing. Few or inappropriate connecting words are used.",
      ],
      [
        "Examples and supporting details are relevant and well-developed using varied vocabulary and complex structures.",
        "Examples and details are mostly relevant with adequate vocabulary and some complex structures.",
        "Examples are limited or irrelevant with basic vocabulary and simple structures only.",
      ],
      [
        "Writing demonstrates excellent control of grammar, punctuation, and spelling. Errors are rare and minor.",
        "Writing shows good control of basic grammar and mechanics. Some errors occur but don't impede understanding.",
        "Frequent grammatical and mechanical errors that interfere with comprehension.",
      ],
    ],
    rows: [
      "Organization & Coherence",
      "Content & Language Use",
      "Accuracy & Mechanics",
    ],
    cols: ["0/2", "1/2", "2/2"],
  },
  "essay-4x4": {
    id: "essay-4x4",
    name: "Default Essay Rubric 4x4",
    content: [
      [
        "Ideas are exceptionally well-organized with sophisticated transitional phrases and cohesive devices.",
        "Ideas are clearly organized with appropriate transitions and good use of cohesive devices.",
        "Ideas show basic organization with simple transitions. Some cohesive devices are attempted.",
        "Ideas lack organization with few or inappropriate transitions. Limited use of cohesive devices.",
      ],
      [
        "Arguments are supported with detailed examples using rich, precise vocabulary and idiomatic expressions.",
        "Arguments are supported with good examples using appropriate vocabulary and some idiomatic expressions.",
        "Arguments have basic support with adequate vocabulary. Few idiomatic expressions are attempted.",
        "Arguments lack support with limited vocabulary. No idiomatic expressions are used.",
      ],
      [
        "Complex grammatical structures are used accurately and appropriately. Errors are minimal.",
        "Mix of complex and simple structures with good accuracy. Errors don't impede understanding.",
        "Mostly simple structures with some errors. Meaning is generally clear despite errors.",
        "Basic structures with frequent errors that make meaning unclear.",
      ],
      [
        "Writing style is engaging with excellent paragraph structure and consistent academic tone.",
        "Writing style is appropriate with good paragraph structure and mostly consistent tone.",
        "Writing style is basic with some paragraph organization. Tone is inconsistent.",
        "Writing style is inappropriate with poor paragraph structure and inconsistent tone.",
      ],
    ],
    rows: [
      "Organization & Transitions",
      "Content & Vocabulary",
      "Grammar & Structures",
      "Style & Format",
    ],
    cols: ["0/3", "1/3", "2/3", "3/3"],
  },
  "essay-5x5": {
    id: "essay-5x5",
    name: "Default Essay Rubric 5x5",
    content: [
      [
        "Text organization is sophisticated with seamless transitions. Cohesive devices are used masterfully.",
        "Text organization is very effective with clear transitions. Cohesive devices are used appropriately.",
        "Text organization is clear with adequate transitions. Basic cohesive devices are used correctly.",
        "Text organization is basic with simple transitions. Cohesive devices are sometimes incorrect.",
        "Text organization is poor with few transitions. Cohesive devices are missing or incorrect.",
      ],
      [
        "Arguments are compelling with extensive supporting details. Advanced vocabulary is used precisely.",
        "Arguments are clear with well-developed support. Vocabulary is varied and appropriate.",
        "Arguments are present with adequate support. Vocabulary is sufficient but may be repetitive.",
        "Arguments are weak with limited support. Vocabulary is basic with some misuse.",
        "Arguments are unclear with minimal support. Vocabulary is very limited or inappropriate.",
      ],
      [
        "Complex grammatical structures are used consistently and accurately throughout.",
        "Mix of complex and simple structures used effectively with minor errors.",
        "Basic grammatical structures are used correctly with some attempts at complexity.",
        "Simple structures dominate with frequent errors in complex forms.",
        "Numerous grammatical errors even in simple structures.",
      ],
      [
        "Academic conventions are followed perfectly. Paragraphing enhances argument flow.",
        "Academic conventions are followed well. Paragraphing supports argument flow.",
        "Academic conventions are mostly followed. Basic paragraphing is present.",
        "Academic conventions are inconsistent. Paragraphing is sometimes unclear.",
        "Academic conventions are not followed. Paragraphing is absent or incorrect.",
      ],
      [
        "Writing demonstrates native-like command of language with sophisticated expression.",
        "Writing shows strong command of language with effective expression.",
        "Writing shows good control of language with clear expression.",
        "Writing shows limited control of language with basic expression.",
        "Writing shows poor control of language with confused expression.",
      ],
    ],
    rows: [
      "Organization & Cohesion",
      "Argumentation & Vocabulary",
      "Grammatical Range & Accuracy",
      "Academic Convention",
      "Language Control",
    ],
    cols: ["0/4", "1/4", "2/4", "3/4", "4/4"],
  },
};
